// Client for the three clinical-extraction endpoints (AI report parsing).
//
// Two endpoints START an extraction over the patient's recently uploaded
// reports (last 24h, `ai_scan_indicator` H/P); the third READS BACK the latest
// usable result. All inputs are query parameters — none of them takes a body.
//
// Auth is injected globally by lib/fetchInterceptor.js, so nothing here sets
// an Authorization header.
//
// See docs/report-parser/api-clinical-extraction.md (revision 3).

import C from "./Config";

export const ExtractionOutcome = {
  SUCCESS: "success", // 200 + serviceStatus SUCCESS — `data.result` is usable
  SUCCESS_STALE: "success_stale", // as SUCCESS, but a NEWER run failed (§4.1.1)
  BUSINESS: "business", // 200 + BUSINESS_VALIDATION_FAILURE — expected, show message
  SYSTEM: "system", // 500 / SYSTEM_FAILURE — generic retry
  UNAUTHORIZED: "unauthorized", // 401 — expired/blacklisted token, NOT a BaseDTO
  BAD_REQUEST: "bad_request", // 400 — malformed patientId, NOT a BaseDTO
};

export const ReportType = {
  DIAGNOSIS: "DIAGNOSIS",
  PATHOLOGY: "PATHOLOGY",
};

// Generic fallback when the run produced nothing and no earlier result exists.
const NOT_AVAILABLE_FALLBACK =
  'Report not available for patient. If report is uploaded in "Case Summary" tab ' +
  "then wait for sometime. If not then please upload the report and check again.";

// A SKIPPED run is reported with the generic "not available" text above, but
// the specific reason survives in `data.errorMessage`. For these two codes the
// specific text is far more actionable — telling the user to wait when nothing
// they uploaded is eligible sends them in the wrong direction. See §4.2, §6.
const PREFER_SPECIFIC_MESSAGE = ["NO_DOCUMENTS", "NO_SUPPORTED_DOCUMENTS"];

// patientId must be present and numeric — a malformed one is rejected by Spring
// with a 400 before the controller runs, so guard rather than send it.
function isValidPatientId(patientId) {
  if (patientId === null || patientId === undefined) return false;
  const asString = String(patientId).trim();
  if (asString === "") return false;
  return /^\d+$/.test(asString);
}

// Reads the body defensively: 401/400 come back in Spring's default error shape
// (no serviceStatus, no data), and a non-JSON body is possible on a proxy error.
async function readBody(response) {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
}

// Collapses an extraction response into { outcome, message, data }.
//
// Order matters: HTTP status is checked FIRST, because 401 and 403 do not carry
// a BaseDTO at all — reading `serviceStatus` unconditionally would throw on an
// expired session instead of sending the user to login (§1).
export function classifyExtractionResponse(httpStatus, body) {
  if (httpStatus === 401) {
    return {
      outcome: ExtractionOutcome.UNAUTHORIZED,
      message: "Your session has expired. Please sign in again.",
      data: null,
    };
  }

  if (httpStatus === 400) {
    return {
      outcome: ExtractionOutcome.BAD_REQUEST,
      message: "The request could not be completed. Please reload and try again.",
      data: null,
    };
  }

  const serviceStatus = body && body.serviceStatus;
  const data = (body && body.data) || null;

  if (serviceStatus === "SUCCESS") {
    // §4.1.1 — a SUCCESS can now carry an OLDER result, resurfaced because a
    // newer run failed. `data.extractionResultId` is then the older run's id,
    // which is the correct thing to stamp on a saved diagnosis: it identifies
    // the run the data actually came from.
    const isStale = !!(data && data.lastAttemptStatus);
    return {
      outcome: isStale
        ? ExtractionOutcome.SUCCESS_STALE
        : ExtractionOutcome.SUCCESS,
      message: (body && body.message) || null,
      data,
    };
  }

  if (serviceStatus === "BUSINESS_VALIDATION_FAILURE") {
    // All errorMsg text is curated by the backend and safe to render verbatim.
    let message = (body && body.errorMsg) || NOT_AVAILABLE_FALLBACK;
    if (
      data &&
      data.errorCode &&
      PREFER_SPECIFIC_MESSAGE.indexOf(data.errorCode) !== -1 &&
      data.errorMessage
    ) {
      message = data.errorMessage;
    }
    return { outcome: ExtractionOutcome.BUSINESS, message, data };
  }

  // SYSTEM_FAILURE, an unexpected status, or an unparseable body.
  return {
    outcome: ExtractionOutcome.SYSTEM,
    message:
      (body && body.errorMsg) ||
      "The extraction service could not be reached. Please try again.",
    data,
  };
}

async function callExtraction(path) {
  const response = await fetch(C.getUrl() + path, {
    method: "POST",
    mode: "cors",
  });
  const body = await readBody(response);
  return classifyExtractionResponse(response.status, body);
}

// POST /extraction/extractDiagnosis — queues a run over today's H-flagged
// reports. Returns in milliseconds; the result takes up to ~3 minutes.
export function startDiagnosisExtraction(patientId) {
  if (!isValidPatientId(patientId)) {
    return Promise.resolve({
      outcome: ExtractionOutcome.BAD_REQUEST,
      message: "Patient is required.",
      data: null,
    });
  }
  return callExtraction(
    "/extraction/extractDiagnosis?patientId=" + encodeURIComponent(patientId)
  );
}

// POST /extraction/extractPathology — same contract, P-flagged reports.
// Completes in seconds rather than minutes.
export function startPathologyExtraction(patientId) {
  if (!isValidPatientId(patientId)) {
    return Promise.resolve({
      outcome: ExtractionOutcome.BAD_REQUEST,
      message: "Patient is required.",
      data: null,
    });
  }
  return callExtraction(
    "/extraction/extractPathology?patientId=" + encodeURIComponent(patientId)
  );
}

// GET /extraction/getExtractionResult — reads the latest usable result.
// Safe to call without triggering anything. `reportType` is case-insensitive
// server-side but we send the canonical upper-case form.
export function getExtractionResult(patientId, reportType) {
  if (!isValidPatientId(patientId)) {
    return Promise.resolve({
      outcome: ExtractionOutcome.BAD_REQUEST,
      message: "Patient is required.",
      data: null,
    });
  }

  const type = reportType || ReportType.DIAGNOSIS;
  const url =
    C.getUrl() +
    "/extraction/getExtractionResult?patientId=" +
    encodeURIComponent(patientId) +
    "&reportType=" +
    encodeURIComponent(type);

  return fetch(url, { method: "GET", mode: "cors" })
    .then(async (response) => {
      const body = await readBody(response);
      return classifyExtractionResponse(response.status, body);
    });
}
